import Service, { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { tracked, cached } from '@glimmer/tracking';
import { GraphModel, MultilineGraphModel, GraphLine } from '@ebiz/ember-aditools-graph';
import type IntlService from 'ember-intl/services/intl';
import { eng3 } from '@ebiz/pretty-num';
import { isNone } from '@ember/utils';
import OpampService from './opamp';
import SensorService from './sensor';
import { SensorData } from '../models/sensor';
import * as PhotodiodeModel from 'photodiode-model/lib/src/model';
import * as ModelUtils from 'photodiode-model/lib/src/utils';
import * as GraphCalculations from 'photodiode-model/lib/src/graphs';
import ModelError from 'photodiode-model/lib/src/models/model-error';
import type {
  PdStage,
  BiasPreference,
  UserInput,
  StageOne,
  StageTwo,
  PhotodiodeSchematic,
  OpAmpSpec as ModelOpAmpSpec,
  OpAmpSelectionItem,
  OpAmpStatusItem as ModelOpAmpStatusItem,
} from 'photodiode-model/lib/src/model';
import type { CircuitGraphDataResult } from 'photodiode-model/lib/src/graphs';
import { getIdealOpAmp1 } from 'photodiode-model/lib/src/recommendations';
import { PSTData, PSTIndex, PSTParameterMapping } from '@ebiz/ember-aditools-device-table/utils/device-table-pst';
import {
  DeviceColumn,
  DeviceData,
  PackedFilterValue,
  SortColumnRef,
} from '@ebiz/ember-aditools-device-table/components/aditools-device-table';

export type OpAmpStatusItem = ModelOpAmpStatusItem;
export type OpAmpSpec = ModelOpAmpSpec;

export class StateModel {
  constructor(copyState?: StateModel) {
    if (copyState) {
      this.Vr = copyState.Vr;
      this.Bias = copyState.Bias;
      this.Cd = copyState.Cd;
      this.Rsh = copyState.Rsh;
      this.Ip = copyState.Ip;
      this.Photodiode = copyState.Photodiode;
      this.StageCount = copyState.StageCount;
      this.OpAmp1 = copyState.OpAmp1;
      this.OpAmp2 = copyState.OpAmp2;
      this.Vp = copyState.Vp;
      this.TargetSpeed = copyState.TargetSpeed;
      this.SpeedPreference = copyState.SpeedPreference;
      this.Peaking = copyState.Peaking;
      this.FrequencyResponseUnit = copyState.FrequencyResponseUnit;
      this.NoiseGainUnit = copyState.NoiseGainUnit;
      this.RequireRecommended = copyState.RequireRecommended;
      this.FirstVisit = copyState.RequireRecommended;
    } else {
      this.Vr = 0;
      this.Bias = '-';
      this.Cd = 1e-10;
      this.Rsh = 1000000000;
      this.Ip = 0.00005;
      this.Photodiode = '';
      this.StageCount = 1;
      this.OpAmp1 = null;
      this.OpAmp2 = null;
      this.Vp = 2;
      this.TargetSpeed = 4e-6;
      this.SpeedPreference = 'PulseWidth';
      this.Peaking = 0.707107;
      this.FrequencyResponseUnit = 'dB';
      this.NoiseGainUnit = 'dB';
      this.RequireRecommended = true;
      this.FirstVisit = true;
    }
  }

  @tracked Vr: number; // Volts
  @tracked Bias: '+' | '-'; // + or -
  @tracked Cd: number; // Farads
  @tracked Rsh: number; //Ohms
  @tracked Ip: number; // Amperes
  @tracked Photodiode: string; //part number or ""
  @tracked StageCount: PdStage;
  @tracked OpAmp1: string | null;
  @tracked OpAmp2: string | null;
  @tracked Vp: number; // Volts
  @tracked TargetSpeed: number; // seconds or Hertz
  @tracked SpeedPreference: string; // "PulseWidth" or "Bandwidth"
  @tracked Peaking: number; // seconds
  @tracked FrequencyResponseUnit: string; // "dB" | "V/A";
  @tracked NoiseGainUnit: string; // "dB" | "V/V";
  @tracked RequireRecommended: boolean;
  @tracked FirstVisit: boolean;
}

type SchematicModel = {
  photodiodeModel: PhotodiodeSchematic;
  stage1: StageOne;
  stage2: StageTwo | null;
};

type PulseResponseModel = {
  riseTime: number;
  overshoot: number;
  graph: GraphModel;
};
type FrequencyResponseModel = {
  fN: number;
  Q: number;
  graph: GraphModel;
};
type SpectralNoiseModel = {
  graph: MultilineGraphModel;
};
type NoiseGainModel = {
  graph: MultilineGraphModel;
};
type SNRModel = {
  peakSignalCurrent: number;
  peakSignalVoltage: number;
  snr: number;
  enob: number;
  minimumSignalCurrent: number;
  outputOffsetVoltage: number;
  stage1Noise: {
    opampVoltage: number;
    opampCurrent: number;
    rf: number;
    rin: number;
    total: number;
  };
  stage2Noise: {
    opampVoltage: number;
    opampCurrent: number;
    rf: number;
    rg: number;
    rFilter: number;
    total: number;
  };
  totalNoise: number;
};

type BOMHash = {
  count: number;
  designators: string[];
  value: string;
  package: string;
  material: string;
  tolerance: string;
  prodURL?: string;
};
type BOMModel = BOMHash[];

type limits = {
  xmax: number;
  xmin: number;
  ymax: number;
  ymin: number;
};

type PowerSupplyRange = {
  min?: number;
  max: number;
};

class SelectionTableFilterOptions {
  constructor() {
    this.columns = undefined;
    this.filters = undefined;
    this.sortColumns = undefined;
  }
  columns: DeviceColumn<DeviceData>[] | undefined;
  filters: PackedFilterValue[] | undefined;
  sortColumns: SortColumnRef[] | undefined;
}

export class OpAmpSelectionDisplayItem {
  constructor() {
    this.selectable = false;
    this.disallowed = true;
    this.partNumber = '';
    this.comment = [];
    this.feedbackMethod = '';
    this.status = '';
    this.statusText = '';
    this.deviceStatus = '';
    this.ltSpice = '';
  }

  selectable: boolean;
  disallowed: boolean;
  comment: OpAmpStatusItem[];
  partNumber: string;
  feedbackMethod: string;
  status: string;
  statusText: string;
  deviceStatus: string;
  ltSpice: string;
}

let isSupportedOpAmp = function (opamps: OpAmpSpec[], partNumber: string): boolean {
  return opamps.some((item) => {
    return item.partNumber === partNumber;
  });
};

export default class ModelService extends Service {
  @service declare intl: IntlService;
  @service('aditools-site') declare siteconfig: { [key: string]: string };
  @service('opamp') declare opampService: OpampService;
  @service('sensor') declare sensorService: SensorService;
  state: StateModel = new StateModel();
  opamps?: OpAmpSpec[] | null;
  sensors?: SensorData[];
  pstData?: PSTData | null;
  pstView?: PSTIndex | null;
  pstMapping?: PSTParameterMapping | null;
  shoppingCartUrl?: string;
  limits?: Limits;
  selectionFilterOptions: SelectionTableFilterOptions = new SelectionTableFilterOptions();

  tryCircuitDesignResults(state: StateModel): CircuitGraphDataResult | ModelError {
    let result = PhotodiodeModel.getResult(
      this.opampdbDTO,
      state.Vr,
      state.Bias == '-' ? 'negative' : 'positive',
      state.Cd,
      state.Rsh,
      state.Ip,
      '',
      state.Vp,
      state.SpeedPreference === 'PulseWidth' ? 2 / state.TargetSpeed : state.TargetSpeed,
      state.Peaking,
      state.StageCount,
      state.OpAmp1,
      state.OpAmp2,
      state.RequireRecommended,
    );

    if (
      this.state.FirstVisit &&
      result instanceof ModelError &&
      result.ErrorCode === 'no_op_amps_available_for_single_stage_too_slow'
    ) {
      result = this.adjustSpeed(result);
    }
    return result;
  }

  private adjustSpeed(result: CircuitGraphDataResult | ModelError): CircuitGraphDataResult | ModelError {
    let speedAltered =
      this.state.SpeedPreference === 'PulseWidth' ? 2 / this.state.TargetSpeed : this.state.TargetSpeed;
    while (
      speedAltered < 200e6 &&
      result instanceof ModelError &&
      result.ErrorCode === 'no_op_amps_available_for_single_stage_too_slow'
    ) {
      speedAltered *= 10;
      this.state.TargetSpeed = this.state.SpeedPreference === 'PulseWidth' ? 2 / speedAltered : speedAltered;
      result = this.tryCircuitDesignResults(this.state);
    }
    return result;
  }

  @cached
  private get circuitDesignResults(): CircuitGraphDataResult | ModelError {
    return this.tryCircuitDesignResults(this.state);
  }

  // The following properties below are called assuming the model has no validation errors
  //   - opAmp1
  //   - opAmp2
  //   - schematicModel
  //   - bom
  //   - pulseResponse
  //   - frequencyResponseVA
  //   - frequencyResponseDb
  //   - spectralNoise
  //   - noiseGainVV
  //   - noiseGainDb
  //   - snr
  // While there is no flag that determines if there are parse errors or model errors,
  // the validate and validateModel functions can be used to determine those.
  // It is the responsibility of the controllers to make sure that these properties are not called
  // in case an error occurs.

  get opAmp1(): OpAmpSpec {
    let opAmpSpec: OpAmpSpec | ModelError;
    if (!(this.circuitDesignResults instanceof ModelError)) {
      opAmpSpec = (this.circuitDesignResults as CircuitGraphDataResult).stagesObject.stage1.opAmp;
    } else {
      opAmpSpec = getIdealOpAmp1(
        this.opampdbDTO,
        this.state.Vr,
        this.state.Bias == '-' ? 'negative' : 'positive',
        this.state.Cd,
        this.state.Rsh,
        this.state.Ip,
        this.state.Photodiode,
        this.state.Vp,
        this.state.SpeedPreference === 'PulseWidth' ? 2 / this.state.TargetSpeed : this.state.TargetSpeed,
        this.state.Peaking,
        this.state.StageCount,
      );

      // Added an assert here. This block doesn't get called unless we have a fixed opamp error
      // but in the event an error occurs, the assert will trigger
      assert('Ideal Op Amp function returns a Model Error', !(opAmpSpec instanceof ModelError));
    }
    return opAmpSpec as OpAmpSpec;
  }

  get opAmp2(): OpAmpSpec | undefined {
    return (this.circuitDesignResults as CircuitGraphDataResult).stagesObject.stage2?.opAmp;
  }

  get opAmpList(): string[] {
    let opAmps = [];
    try {
      opAmps.push(this.state.OpAmp1 ? this.state.OpAmp1 : '');
      if (this.opAmp2) {
        opAmps.push(this.state.OpAmp2 ? this.state.OpAmp2 : '');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      opAmps = [];
    }
    return opAmps;
  }

  @cached
  get schematicModel(): SchematicModel {
    let photodiodeModel = PhotodiodeModel.getSchematicFromInput(
      this.state.Vr,
      this.state.Bias,
      this.state.Cd,
      this.state.Rsh,
      this.state.Ip,
      this.state.Photodiode,
    );
    let stagesObject = (this.circuitDesignResults as CircuitGraphDataResult).stagesObject;
    return {
      photodiodeModel: photodiodeModel,
      stage1: stagesObject.stage1,
      stage2: stagesObject.stage2,
    };
  }

  get bom(): BOMModel {
    let circuitDesignResults = this.circuitDesignResults as CircuitGraphDataResult;
    let bomArr = PhotodiodeModel.billOfMaterials(
      circuitDesignResults.stagesObject,
      this.state.StageCount,
      this.state.Photodiode,
    );
    for (const data of bomArr) {
      if (data.material === 'tantalum') {
        data.material = this.intl.t('figures.bom.materials.tantalum').toString();
      }
      data.prodURL = data.prodURL
        ? this.siteconfig['SiteUrl'] + '/' + this.siteconfig['AdiLocale'] + '/' + data.prodURL
        : undefined;
    }
    return bomArr;
  }

  get pulseResponse(): PulseResponseModel {
    let circuitDesignResults = this.circuitDesignResults as CircuitGraphDataResult;
    let graphData = ModelUtils.joinArrays(circuitDesignResults.timeAxis, circuitDesignResults.stepResponse);
    let limits = this.addMargins(ModelUtils.getRangeLimits(graphData));
    return {
      riseTime: circuitDesignResults.riseTime,
      overshoot: circuitDesignResults.overshoot,
      graph: {
        limits: limits,
        data: graphData,
      },
    };
  }

  get frequencyResponseVA(): FrequencyResponseModel {
    let circuitDesignResults = this.circuitDesignResults as CircuitGraphDataResult;
    let stages = circuitDesignResults.stagesObject;
    let fnAndQ = GraphCalculations.calculateNaturalFrequencyAndQ(stages, this.state.StageCount);
    let frequencyArray = circuitDesignResults.frequency;
    let magnitudeArray = circuitDesignResults.magnitude;
    let graphData = ModelUtils.joinArrays(frequencyArray, magnitudeArray);
    let limits = this.addLogMargins(ModelUtils.getRangeLimits(graphData));
    return {
      fN: fnAndQ.fn,
      Q: fnAndQ.q,
      graph: {
        limits: limits,
        data: graphData,
      },
    };
  }

  get frequencyResponseDb(): FrequencyResponseModel {
    let circuitDesignResults = this.circuitDesignResults as CircuitGraphDataResult;
    let stages = circuitDesignResults.stagesObject;
    let fnAndQ = GraphCalculations.calculateNaturalFrequencyAndQ(stages, this.state.StageCount);
    let frequencyArray = circuitDesignResults.frequency;
    let magnitudeArray = circuitDesignResults.magnitude;
    let magnitudeDbArray = ModelUtils.VAarrtoDBarr(magnitudeArray);
    let graphData = ModelUtils.joinArrays(frequencyArray, magnitudeDbArray);
    let limits = this.addMargins(ModelUtils.getRangeLimits(graphData));
    return {
      fN: fnAndQ.fn,
      Q: fnAndQ.q,
      graph: {
        limits: limits,
        data: graphData,
      },
    };
  }

  get spectralNoise(): SpectralNoiseModel {
    let stages = (this.circuitDesignResults as CircuitGraphDataResult).stagesObject;
    let frequencyArray = (this.circuitDesignResults as CircuitGraphDataResult).frequency;
    let graphData = GraphCalculations.getSpectralNoiseData(frequencyArray, stages) as Array<GraphLine>;
    if (graphData.length > 8) {
      graphData.splice(7, 2); // Remove rf2 and rg from the data to deliver
    }
    let limits = this.addLogMargins(ModelUtils.getMultilineLimits(graphData));
    return {
      graph: {
        limits: limits,
        data: graphData,
      },
    };
  }

  get noiseGainVV(): NoiseGainModel {
    let stages = (this.circuitDesignResults as CircuitGraphDataResult).stagesObject;
    let frequencyArray = (this.circuitDesignResults as CircuitGraphDataResult).frequency;
    let noiseGraphData = GraphCalculations.calculateArraysForNoiseGainPlot(frequencyArray, stages.stage1);
    let graphData = [];
    graphData[0] = ModelUtils.joinArrays(frequencyArray, noiseGraphData[0] as number[]);
    graphData[1] = ModelUtils.joinArrays(frequencyArray, noiseGraphData[1] as number[]);
    graphData[2] = ModelUtils.joinArrays(frequencyArray, noiseGraphData[2] as number[]);
    graphData[3] = ModelUtils.joinArrays(frequencyArray, noiseGraphData[3] as number[]);
    let limits = this.addLogMargins(ModelUtils.getMultilineLimits(graphData));
    limits.ymax = limits.ymax + 0.0000001;
    return {
      graph: {
        limits: limits,
        data: graphData,
      },
    };
  }

  get noiseGainDb(): NoiseGainModel {
    let stages = (this.circuitDesignResults as CircuitGraphDataResult).stagesObject;
    let frequencyArray = (this.circuitDesignResults as CircuitGraphDataResult).frequency;
    let noiseGraphData = GraphCalculations.calculateArraysForNoiseGainPlot(frequencyArray, stages.stage1);
    let graphData = [];
    graphData[0] = ModelUtils.joinArrays(frequencyArray, ModelUtils.VVarrtoDBarr(noiseGraphData[0] as number[]));
    graphData[1] = ModelUtils.joinArrays(frequencyArray, ModelUtils.VVarrtoDBarr(noiseGraphData[1] as number[]));
    graphData[2] = ModelUtils.joinArrays(frequencyArray, ModelUtils.VVarrtoDBarr(noiseGraphData[2] as number[]));
    graphData[3] = ModelUtils.joinArrays(frequencyArray, ModelUtils.VVarrtoDBarr(noiseGraphData[3] as number[]));
    let limits = ModelUtils.getMultilineLimits(graphData);
    limits.ymax = limits.ymax + 0.0000001;
    return {
      graph: {
        limits: limits,
        data: graphData,
      },
    };
  }

  get snr(): SNRModel {
    let circuitDesignResults = this.circuitDesignResults as CircuitGraphDataResult;
    return {
      peakSignalCurrent: this.state.Ip,
      peakSignalVoltage: this.state.Vp,
      snr: circuitDesignResults.dynamicRange.snr,
      enob: circuitDesignResults.dynamicRange.enob,
      minimumSignalCurrent: circuitDesignResults.dynamicRange.minPhotoCurrent,
      outputOffsetVoltage: circuitDesignResults.dynamicRange.outputOffset,
      stage1Noise: {
        opampVoltage: circuitDesignResults.dynamicRange.noise.stage1.opAmpV,
        opampCurrent: circuitDesignResults.dynamicRange.noise.stage1.opAmpI,
        rf: circuitDesignResults.dynamicRange.noise.stage1.rf,
        rin: circuitDesignResults.dynamicRange.noise.stage1.rin,
        total: circuitDesignResults.dynamicRange.noise.stage1.total,
      },
      stage2Noise: {
        opampVoltage: circuitDesignResults.dynamicRange.noise.stage2.opAmpV,
        opampCurrent: circuitDesignResults.dynamicRange.noise.stage2.opAmpI,
        rf: circuitDesignResults.dynamicRange.noise.stage2.rf,
        rg: circuitDesignResults.dynamicRange.noise.stage2.rg,
        rFilter: circuitDesignResults.dynamicRange.noise.stage2.rfilter,
        total: circuitDesignResults.dynamicRange.noise.stage2.total,
      },
      totalNoise: circuitDesignResults.dynamicRange.noise.total,
    };
  }

  get peakingLimits(): { min: number; max: number } {
    return { min: 0.166666, max: 1.33333 };
  }

  @cached
  get availableOpAmps(): { stage1: OpAmpSelectionDisplayItem[]; stage2: OpAmpSelectionDisplayItem[] } {
    if (this.opamps === undefined) throw 'Op amps source is undefined. ';
    let biasPreference: BiasPreference = 'positive';
    if (this.state.Bias == '-') biasPreference = 'negative';
    let targetSpeed = this.state.SpeedPreference === 'PulseWidth' ? 2 / this.state.TargetSpeed : this.state.TargetSpeed; // Always pass the bandwidth to the model
    let stage1OpAmpList = PhotodiodeModel.getAvailableOpAmps(
      this.opampdbDTO,
      this.state.Vr,
      biasPreference,
      this.state.Cd,
      this.state.Rsh,
      this.state.Ip,
      '',
      this.state.Vp,
      targetSpeed,
      this.state.Peaking,
      this.state.StageCount,
      this.state.OpAmp1 ? this.state.OpAmp1 : this.opAmp1.partNumber,
      1,
    );
    let stage2OpAmpList =
      this.state.StageCount === 2
        ? PhotodiodeModel.getAvailableOpAmps(
            this.opampdbDTO,
            this.state.Vr,
            biasPreference,
            this.state.Cd,
            this.state.Rsh,
            this.state.Ip,
            '',
            this.state.Vp,
            targetSpeed,
            this.state.Peaking,
            this.state.StageCount,
            this.state.OpAmp1 ? this.state.OpAmp1 : this.opAmp1.partNumber,
            2,
          )
        : stage1OpAmpList;
    let resultStage1 = this.convertToOpAmpList(this.opamps, stage1OpAmpList);
    let resultStage2 = this.convertToOpAmpList(this.opamps, stage2OpAmpList);
    return { stage1: resultStage1, stage2: resultStage2 };
  }

  private convertToOpAmpList(
    opampDB: OpAmpSpec[] | null | undefined,
    opAmpList: OpAmpSelectionItem[],
  ): OpAmpSelectionDisplayItem[] {
    if (opampDB === undefined || opampDB === null) throw 'Opamp DB should not be undefined. ';

    let result = new Array<OpAmpSelectionDisplayItem>();
    for (const opAmpItem of opAmpList) {
      let opAmpDbInfo = opampDB.find((p: OpAmpSpec) => p.partNumber == opAmpItem.partNumber);

      if (opAmpDbInfo === undefined) continue;

      let item = new OpAmpSelectionDisplayItem();
      item.partNumber = opAmpItem.partNumber;
      item.selectable = opAmpItem.selectable;
      item.disallowed = opAmpItem.disallowed;
      item.comment = opAmpItem.comment;
      item.feedbackMethod = opAmpDbInfo.feedbackMethod;
      item.status = opAmpItem.status;
      item.statusText = opAmpDbInfo.statusText;
      item.deviceStatus = `${opAmpDbInfo.deviceStatus}`;
      item.ltSpice = opAmpDbInfo.spice ? 'Yes' : 'No';
      result.push(item);
    }

    return result;
  }

  private get opampdbDTO(): OpAmpSpec[] {
    if (typeof this.opamps === 'undefined') {
      throw 'op amps collection never completed request - cannot proceed';
    } else if (this.opamps === null) {
      throw 'op amps collection failed - cannot proceed';
    }
    return this.opamps;
  }

  @action validate(e: {
    key: string;
    newValue: number | string | PdStage;
    oldValue: number | string | PdStage;
    changes: { [key: string]: string | number | PdStage };
    content: StateModel;
    tab: string;
  }): boolean | string {
    let result: boolean | string = true;
    let speedPreference = '';
    let minLimit;
    let maxLimit;
    if (e.newValue != e.oldValue) {
      if (!isNone(this.limits)) {
        let newContent = new StateModel(e.content);
        switch (e.key) {
          case 'Vr':
            {
              const reverseVoltage = this.limits['reverseVoltage'] as LimitDetails;
              if (this.isRangeValid(e.newValue as number, reverseVoltage)) {
                newContent.Vr = e.newValue as number;
              } else {
                result = this.intl.t('validation-error.reverse-voltage', {
                  min: eng3(reverseVoltage.min),
                  max: eng3(reverseVoltage.max),
                });
              }
            }
            break;
          case 'Cd':
            {
              const capacitance = this.limits['capacitance'] as LimitDetails;
              if (this.isRangeValid(e.newValue as number, capacitance)) {
                newContent.Cd = e.newValue as number;
              } else {
                result = this.intl.t('validation-error.capacitance', {
                  min: eng3(capacitance.min),
                  max: eng3(capacitance.max),
                });
              }
            }
            break;
          case 'Rsh':
            {
              const shuntResistance = this.limits['shuntResistance'] as LimitDetails;
              if (this.isRangeValid(e.newValue as number, shuntResistance)) {
                newContent.Rsh = e.newValue as number;
              } else {
                result = this.intl.t('validation-error.shunt-resistance', {
                  min: eng3(shuntResistance.min),
                  max: eng3(shuntResistance.max),
                });
              }
            }
            break;
          case 'Ip':
            {
              const peakCurrent = this.limits['peakCurrent'] as LimitDetails;
              if (this.isRangeValid(e.newValue as number, peakCurrent)) {
                newContent.Ip = e.newValue as number;
              } else {
                result = this.intl.t('validation-error.peak-current', {
                  min: eng3(peakCurrent.min),
                  max: eng3(peakCurrent.max),
                });
              }
            }
            break;
          case 'Vp':
            {
              const peakVoltage = this.limits['peakVoltage'] as LimitDetails;
              if (this.isRangeValid(e.newValue as number, peakVoltage)) {
                newContent.Vp = e.newValue as number;
              } else {
                result = this.intl.t('validation-error.peak-voltage', {
                  min: eng3(peakVoltage.min),
                  max: eng3(peakVoltage.max),
                });
              }
            }
            break;
          case 'TargetSpeed':
            {
              const limitSpeed = this.limits['speed'] as LimitDetails;
              speedPreference = (e.changes['SpeedPreference'] as string)
                ? (e.changes['SpeedPreference'] as string)
                : (e.content.SpeedPreference as string);
              minLimit = speedPreference === 'Bandwidth' ? limitSpeed.min : 2 / limitSpeed.max;
              maxLimit = speedPreference === 'Bandwidth' ? limitSpeed.max : 2 / limitSpeed.min;
              if (this.isRangeValid(e.newValue as number, { min: minLimit, max: maxLimit })) {
                newContent.TargetSpeed = e.newValue as number;
              } else {
                result = this.intl.t('validation-error.speed', {
                  min: eng3(minLimit),
                  max: eng3(maxLimit),
                });
              }
            }
            break;
          case 'OpAmp1':
            newContent.OpAmp1 = e.newValue as string;
            break;
          case 'OpAmp2':
            newContent.OpAmp2 = e.newValue as string;
            break;
          case 'Peaking':
            newContent.Peaking = Number(e.newValue);
            break;
          case 'StageCount':
            newContent.StageCount = e.newValue as PdStage;
            break;
        }
      } else {
        result = 'Limits are required';
      }
    }
    return result;
  }

  @action validateLoadDesign(e: {
    key: string;
    newValue: number | string | PdStage;
    oldValue: number | string | PdStage;
    changes: { [key: string]: string | number | PdStage };
    content: StateModel;
    tab: string;
  }): boolean | string {
    let result: boolean | string = true;
    let speedPreference = '';
    let minLimit;
    let maxLimit;
    if (e.newValue != e.oldValue) {
      if (!isNone(this.limits)) {
        switch (e.key) {
          case 'Vr':
            {
              const reverseVoltage = this.limits['reverseVoltage'] as LimitDetails;
              if (!isNaN(e.newValue as number)) {
                if (!this.isRangeValid(e.newValue as number, reverseVoltage)) {
                  result = this.intl.t('validation-error.reverse-voltage', {
                    min: eng3(reverseVoltage.min),
                    max: eng3(reverseVoltage.max),
                  });
                }
              } else {
                result = this.intl.t('validation-error.invalid-input-error', { value: e.newValue, field: 'Vr' });
              }
            }
            break;
          case 'Cd':
            {
              const capacitance = this.limits['capacitance'] as LimitDetails;
              if (!isNaN(e.newValue as number)) {
                if (!this.isRangeValid(e.newValue as number, capacitance)) {
                  result = this.intl.t('validation-error.capacitance', {
                    min: eng3(capacitance.min),
                    max: eng3(capacitance.max),
                  });
                }
              } else {
                result = this.intl.t('validation-error.invalid-input-error', { value: e.newValue, field: 'Cd' });
              }
            }
            break;
          case 'Rsh':
            {
              const shuntResistance = this.limits['shuntResistance'] as LimitDetails;
              if (!isNaN(e.newValue as number)) {
                if (!this.isRangeValid(e.newValue as number, shuntResistance)) {
                  result = this.intl.t('validation-error.shunt-resistance', {
                    min: eng3(shuntResistance.min),
                    max: eng3(shuntResistance.max),
                  });
                }
              } else {
                result = this.intl.t('validation-error.invalid-input-error', { value: e.newValue, field: 'Rsh' });
              }
            }
            break;
          case 'Ip':
            {
              const peakCurrent = this.limits['peakCurrent'] as LimitDetails;
              if (!isNaN(e.newValue as number)) {
                if (!this.isRangeValid(e.newValue as number, peakCurrent)) {
                  result = this.intl.t('validation-error.peak-current', {
                    min: eng3(peakCurrent.min),
                    max: eng3(peakCurrent.max),
                  });
                }
              } else {
                result = this.intl.t('validation-error.invalid-input-error', { value: e.newValue, field: 'Ip' });
              }
            }
            break;
          case 'Vp':
            {
              const peakVoltage = this.limits['peakVoltage'] as LimitDetails;
              if (!isNaN(e.newValue as number)) {
                if (!this.isRangeValid(e.newValue as number, peakVoltage)) {
                  result = this.intl.t('validation-error.peak-voltage', {
                    min: eng3(peakVoltage.min),
                    max: eng3(peakVoltage.max),
                  });
                }
              } else {
                result = this.intl.t('validation-error.invalid-input-error', { value: e.newValue, field: 'Vp' });
              }
            }
            break;
          case 'TargetSpeed':
            {
              const limitSpeed = this.limits['speed'] as LimitDetails;
              speedPreference = (e.changes['SpeedPreference'] as string)
                ? (e.changes['SpeedPreference'] as string)
                : (e.content.SpeedPreference as string);
              minLimit = speedPreference === 'Bandwidth' ? limitSpeed.min : 2 / limitSpeed.max;
              maxLimit = speedPreference === 'Bandwidth' ? limitSpeed.max : 2 / limitSpeed.min;
              if (!isNaN(e.newValue as number)) {
                if (!this.isRangeValid(e.newValue as number, { min: minLimit, max: maxLimit })) {
                  result = this.intl.t('validation-error.speed', {
                    min: eng3(minLimit),
                    max: eng3(maxLimit),
                  });
                }
              } else {
                result = this.intl.t('validation-error.invalid-input-error', {
                  value: e.newValue,
                  field: 'TargetSpeed',
                });
              }
            }
            break;
          case 'OpAmp1':
          case 'OpAmp2':
            if (e.newValue) {
              if (!isSupportedOpAmp(this.opamps as OpAmpSpec[], e.newValue as string)) {
                result = `OpAmpError:${e.newValue}`;
              }
            }
            break;
          case 'StageCount':
            if (isNaN(e.newValue as number)) {
              result = this.intl.t('validation-error.invalid-input-error', { value: e.newValue, field: 'StageCount' });
            }
            break;
        }
      } else {
        result = 'Limits are required';
      }
    }
    return result;
  }

  @action validateModel(state: StateModel): ModelError | undefined {
    let modelResult: CircuitGraphDataResult | ModelError = this.tryCircuitDesignResults(state);
    let result = 'ErrorCode' in modelResult ? (modelResult as ModelError) : undefined;
    if (result?.ErrorParameter) {
      result.ErrorParameter.statusMessage?.forEach((status: { key: string }) => {
        status.key = 'model-error.' + status.key;
      });
      result.ErrorParameter.marginalStatusMessage?.forEach((status: { key: string }) => {
        status.key = 'model-error.' + status.key;
      });
    }
    return result;
  }

  addMargins(limits: limits): limits {
    let newLimits = { ...limits };
    let newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    let newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    newLimits.ymax = newYmax;
    newLimits.ymin = newYmin;

    return newLimits;
  }

  addLogMargins(limits: limits): limits {
    let newLimits = { ...limits };
    let newYmax = Math.pow(10, Math.floor(Math.log10(limits.ymax) + 1));
    let newYmin = Math.pow(10, Math.floor(Math.log10(limits.ymin - limits.ymin / 10)));
    newLimits.ymax = newYmax;
    newLimits.ymin = newYmin;

    return newLimits;
  }

  isRangeValid(value: number | null, limitDetails: LimitDetails): boolean {
    let valid = false;
    if (typeof value === 'number') {
      valid = value >= limitDetails.min && value <= limitDetails.max;
    }
    return valid;
  }

  get powerSupplyRange(): PowerSupplyRange {
    let userInput: UserInput = {
      opAmps: this.opAmpList,
      stages: this.state.StageCount,
      peaking: this.state.Peaking,
      speed: this.state.TargetSpeed,
      peakVoltage: this.state.Vp,
      peakCurrent: this.state.Ip,
      shuntResistance: this.state.Rsh,
      capacitance: this.state.Cd,
      biasPreference: this.state.Bias == '-' ? 'negative' : 'positive',
      reverseVoltage: this.state.Vr,
      sensor: this.state.Photodiode,
    };
    let stage1 = this.schematicModel.stage1;
    if (this.state.StageCount === 2) {
      let stage2 = this.schematicModel.stage2 as StageTwo;
      return {
        min: GraphCalculations.calculateMinSupplyVoltage(userInput, stage1, stage2),
        max: GraphCalculations.calculateMaxSupplyVoltage(userInput, stage1, stage2),
      };
    } else {
      return {
        min: GraphCalculations.calculateMinSupplyVoltage(userInput, stage1),
        max: GraphCalculations.calculateMaxSupplyVoltage(userInput, stage1),
      };
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    model: ModelService;
  }
}
